import React, { useEffect, useState } from "react";
import { Package, Plus, RefreshCw, Search, Sparkles, X } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

const statusLabel = {
  active: "Activo",
  staff_only: "Solo Staff",
  hidden: "Oculto",
  coming_soon: "Próximamente",
};

const statusClass = {
  active: "bg-emerald-100 text-emerald-800",
  staff_only: "bg-blue-100 text-blue-800",
  hidden: "bg-slate-100 text-slate-600",
  coming_soon: "bg-purple-100 text-purple-800",
};

function money(value) {
  if (value === null || value === undefined || value === "") return "—";
  return `${Number(value || 0).toFixed(2)} €`;
}

function minutes(seconds) {
  const n = Number(seconds || 0);
  if (!n) return "—";
  if (n < 60) return `${n}s`;
  return `${Math.round(n / 60)} min`;
}

const catalogCategories = [
  ["impresion", "Impresión"],
  ["papel", "Papel / materiales"],
  ["tamano", "Tamaño"],
  ["soportes", "Soportes rígidos"],
  ["encuadernacion", "Encuadernación"],
  ["plastificado", "Plastificado"],
  ["fotografia", "Fotografía"],
  ["escaneado", "Escaneado"],
  ["plotter", "Plotter / gran formato"],
  ["vinilo", "Vinilo"],
  ["pegatinas", "Pegatinas"],
  ["tfg_tesis", "TFG / tesis"],
  ["configuradores", "Configuradores cliente"],
  ["diseno", "Diseño"],
  ["extras", "Extras"],
  ["general", "General"],
];

const categoryOrder = {
  impresion: 1,
  papel: 2,
  tamano: 3,
  soportes: 4,
  encuadernacion: 5,
  plastificado: 6,
  fotografia: 7,
  escaneado: 8,
  plotter: 9,
  vinilo: 10,
  diseno: 11,
  extras: 99,
  general: 100,
};

const configuratorOptions = [
  ["copies", "Copias"],
  ["color", "B/N o color"],
  ["sides", "Una cara / dos caras"],
  ["size", "Tamaño"],
  ["paper", "Material / papel"],
  ["stapling", "Grapado"],
  ["binding", "Encuadernación"],
  ["lamination", "Plastificado"],
  ["perforation", "Perforado"],
  ["cutting", "Corte"],
  ["folding", "Doblado"],
  ["foam_board", "Cartón pluma"],
  ["engraving", "Grabado"],
];

const emptyForm = {
  name: "",
  icon: "",
  category: "general",
  status: "staff_only",
  pricing_type: "fixed",
  unit: "unidad",
  price_eur: "",
  cost_eur: "",
  vat_percent: "21",
  estimated_time_seconds: "",
  production_points: "",
  visible_client: false,
  visible_staff: true,
  description: "",
  options: [],
  default_configuration: {
    copies: 1,
    color: "bw",
    sides: "double",
    size: "A4",
    paper: "papel-80g",
    finishes: [],
  },
  price_rules: [],
  special_price_rules: [],
  service_type: "service",
  calculation_basis: "unit",
  independent_service: true,
  attachable_service: false,
  min_sheets: "",
  max_sheets: "",
};

export default function AdminServiceCatalog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [catalogTab, setCatalogTab] = useState("general");
  const [clientTags, setClientTags] = useState([]);
  const [loyaltyLevels, setLoyaltyLevels] = useState([]);
  const [newTagName, setNewTagName] = useState("");
  const [newLevelName, setNewLevelName] = useState("");
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/admin/service-catalog");
      setItems(data || []);
    } catch {
      toast.error("No se pudo cargar el catálogo comercial");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/admin/client-tags")
      .then(({ data }) => setClientTags(data.tags || []))
      .catch(() => setClientTags([]));

    api.get("/admin/loyalty-levels")
      .then(({ data }) => setLoyaltyLevels(data.levels || []))
      .catch(() => setLoyaltyLevels([]));

    api.get("/admin/clients")
      .then(({ data }) => setClients(data || []))
      .catch(() => setClients([]));
  }, []);

  const seed = async () => {
    try {
      setSeeding(true);
      const { data } = await api.post("/admin/service-catalog/seed");
      toast.success(`Catálogo creado: ${data.created} nuevos · ${data.skipped} existentes`);
      await load();
    } catch {
      toast.error("No se pudo crear el catálogo de ejemplo");
    } finally {
      setSeeding(false);
    }
  };

  const openEdit = (item) => {
    setEditing(item);
    setCatalogTab("general");
    setForm({
      name: item.name || "",
      icon: item.icon || "",
      category: item.category || "general",
      status: item.status || "staff_only",
      pricing_type: item.pricing_type || "fixed",
      unit: item.unit || "unidad",
      price_eur: item.price_eur ?? "",
      cost_eur: item.cost_eur ?? "",
      vat_percent: item.vat_percent ?? 21,
      estimated_time_seconds: item.estimated_time_seconds ?? "",
      production_points: item.production_points ?? "",
      visible_client: !!item.visible_client,
      visible_staff: item.visible_staff !== false,
      description: item.description || "",
      options: item.options || [],
      default_configuration: item.default_configuration || {
        copies: 1,
        color: "bw",
        sides: "double",
        size: "A4",
        paper: "papel-80g",
        finishes: [],
      },
      price_rules: item.price_rules || [],
      special_price_rules: item.special_price_rules || [],
      service_type: item.service_type || "service",
      calculation_basis: item.calculation_basis || "unit",
      independent_service: item.independent_service !== false,
      attachable_service: !!item.attachable_service,
      min_sheets: item.min_sheets ?? "",
      max_sheets: item.max_sheets ?? "",
    });
  };

  const duplicateService = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      const payload = {
        ...form,
        name: `${form.name || editing.name} (copia)`,
        slug: "",
        price_eur: form.price_eur === "" ? null : Number(String(form.price_eur).replace(",", ".")),
        cost_eur: form.cost_eur === "" ? null : Number(String(form.cost_eur).replace(",", ".")),
        vat_percent: form.vat_percent === "" ? null : Number(String(form.vat_percent).replace(",", ".")),
        estimated_time_seconds: form.estimated_time_seconds === "" ? 0 : Number(form.estimated_time_seconds),
        production_points: form.production_points === "" ? 0 : Number(form.production_points),
        min_sheets: form.min_sheets === "" ? 0 : Number(form.min_sheets),
        max_sheets: form.max_sheets === "" ? 0 : Number(form.max_sheets),
      };
      const { data } = await api.post("/admin/service-catalog", payload);
      setItems((prev) => [data, ...prev]);
      openEdit(data);
      toast.success("Servicio duplicado");
    } catch {
      toast.error("No se pudo duplicar el servicio");
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      const payload = {
        ...form,
        price_eur: form.price_eur === "" ? null : Number(String(form.price_eur).replace(",", ".")),
        cost_eur: form.cost_eur === "" ? null : Number(String(form.cost_eur).replace(",", ".")),
        vat_percent: form.vat_percent === "" ? null : Number(String(form.vat_percent).replace(",", ".")),
        estimated_time_seconds: form.estimated_time_seconds === "" ? 0 : Number(form.estimated_time_seconds),
        production_points: form.production_points === "" ? 0 : Number(form.production_points),
        default_configuration: form.default_configuration || {},
        price_rules: (form.price_rules || []).map((r) => ({
          ...r,
          from: r.from === "" || r.from === null || r.from === undefined ? null : Number(r.from),
          to: r.to === "" || r.to === null || r.to === undefined ? null : Number(r.to),
          unit_price_eur: r.unit_price_eur === "" || r.unit_price_eur === null || r.unit_price_eur === undefined ? null : Number(String(r.unit_price_eur).replace(",", ".")),
        })),
        service_type: form.service_type || "service",
        calculation_basis: form.calculation_basis || "unit",
        independent_service: !!form.independent_service,
        attachable_service: !!form.attachable_service,
        min_sheets: form.min_sheets === "" ? 0 : Number(form.min_sheets),
        max_sheets: form.max_sheets === "" ? 0 : Number(form.max_sheets),
        special_price_rules: (form.special_price_rules || []).map((sp) => ({
          ...sp,
          price_rules: (sp.price_rules || []).map((r) => ({
            ...r,
            from: r.from === "" || r.from === null || r.from === undefined ? null : Number(r.from),
            to: r.to === "" || r.to === null || r.to === undefined ? null : Number(r.to),
            unit_price_eur: r.unit_price_eur === "" || r.unit_price_eur === null || r.unit_price_eur === undefined ? null : Number(String(r.unit_price_eur).replace(",", ".")),
          })),
        })),
      };
      const { data } = await api.put(`/admin/service-catalog/${editing.id}`, payload);
      setItems((prev) => prev.map((x) => x.id === data.id ? data : x));
      setEditing(null);
      toast.success("Servicio actualizado");
    } catch {
      toast.error("No se pudo guardar el servicio");
    } finally {
      setSaving(false);
    }
  };

  const updateRule = (idx, key, value) => {
    setForm((prev) => {
      const rules = [...(prev.price_rules || [])];
      rules[idx] = { ...(rules[idx] || {}), [key]: value };
      return { ...prev, price_rules: rules };
    });
  };

  const addRule = () => {
    setForm((prev) => ({
      ...prev,
      price_rules: [...(prev.price_rules || []), { from: "", to: "", unit_price_eur: "" }],
    }));
  };

  const removeRule = (idx) => {
    setForm((prev) => ({
      ...prev,
      price_rules: (prev.price_rules || []).filter((_, i) => i !== idx),
    }));
  };

  const updateDefaultConfig = (key, value) => {
    setForm((prev) => ({
      ...prev,
      default_configuration: {
        ...(prev.default_configuration || {}),
        [key]: value,
      },
    }));
  };

  const toggleDefaultFinish = (slug) => {
    setForm((prev) => {
      const cfg = prev.default_configuration || {};
      const current = Array.isArray(cfg.finishes) ? cfg.finishes : [];
      const next = current.includes(slug)
        ? current.filter((x) => x !== slug)
        : [...current, slug];

      return {
        ...prev,
        default_configuration: {
          ...cfg,
          finishes: next,
        },
      };
    });
  };

  const toggleConfiguratorOption = (key, label) => {
    setForm((prev) => {
      const current = prev.options || [];
      const exists = current.some((o) => o.key === key);
      const next = exists
        ? current.filter((o) => o.key !== key)
        : [...current, { key, label, enabled: true, type: "boolean" }];
      return { ...prev, options: next };
    });
  };

  const updateSpecial = (idx, key, value) => {
    setForm((prev) => {
      const list = [...(prev.special_price_rules || [])];
      list[idx] = { ...(list[idx] || {}), [key]: value };
      return { ...prev, special_price_rules: list };
    });
  };

  const updateSpecialRule = (spIdx, ruleIdx, key, value) => {
    setForm((prev) => {
      const list = [...(prev.special_price_rules || [])];
      const sp = { ...(list[spIdx] || {}) };
      const rules = [...(sp.price_rules || [])];
      rules[ruleIdx] = { ...(rules[ruleIdx] || {}), [key]: value };
      sp.price_rules = rules;
      list[spIdx] = sp;
      return { ...prev, special_price_rules: list };
    });
  };

  const addSpecialRate = () => {
    setForm((prev) => ({
      ...prev,
      special_price_rules: [
        ...(prev.special_price_rules || []),
        {
          name: "Estudiantes",
          condition_type: "client_tag",
          condition_value: "estudiante",
          price_rules: [{ from: "", to: "", unit_price_eur: "" }],
        },
      ],
    }));
  };

  const removeSpecialRate = (idx) => {
    setForm((prev) => ({
      ...prev,
      special_price_rules: (prev.special_price_rules || []).filter((_, i) => i !== idx),
    }));
  };

  const duplicateSpecialRate = (idx) => {
    setForm((prev) => {
      const list = [...(prev.special_price_rules || [])];
      const copy = JSON.parse(JSON.stringify(list[idx] || {}));
      copy.name = `${copy.name || "Tarifa especial"} (copia)`;
      list.splice(idx + 1, 0, copy);
      return { ...prev, special_price_rules: list };
    });
  };

  const addSpecialRule = (spIdx) => {
    setForm((prev) => {
      const list = [...(prev.special_price_rules || [])];
      const sp = { ...(list[spIdx] || {}) };
      sp.price_rules = [...(sp.price_rules || []), { from: "", to: "", unit_price_eur: "" }];
      list[spIdx] = sp;
      return { ...prev, special_price_rules: list };
    });
  };

  const removeSpecialRule = (spIdx, ruleIdx) => {
    setForm((prev) => {
      const list = [...(prev.special_price_rules || [])];
      const sp = { ...(list[spIdx] || {}) };
      sp.price_rules = (sp.price_rules || []).filter((_, i) => i !== ruleIdx);
      list[spIdx] = sp;
      return { ...prev, special_price_rules: list };
    });
  };

  const createClientTag = async (spIdx) => {
    const tag = String(newTagName || "").trim().toLowerCase().replace(/\s+/g, "_");
    if (!tag) {
      toast.error("Escribe una etiqueta");
      return;
    }

    const next = Array.from(new Set([...(clientTags || []), tag]));
    try {
      const { data } = await api.put("/admin/client-tags", { tags: next });
      setClientTags(data.tags || next);
      updateSpecial(spIdx, "condition_value", tag);
      setNewTagName("");
      toast.success("Etiqueta creada");
    } catch {
      toast.error("No se pudo crear la etiqueta");
    }
  };

  const createLoyaltyLevel = async (spIdx) => {
    const label = String(newLevelName || "").trim();
    if (!label) {
      toast.error("Escribe un nivel");
      return;
    }

    const id = label.toUpperCase().replace(/\s+/g, "_");
    const next = [
      ...(loyaltyLevels || []),
      {
        id,
        label,
        icon: "⭐",
        min_orders: 0,
        min_pages: 0,
        mode: "MANUAL",
        manual_only: true,
        sort_order: (loyaltyLevels || []).length + 1,
      },
    ];

    try {
      const { data } = await api.put("/admin/loyalty-levels", { levels: next });
      setLoyaltyLevels(data.levels || next);
      updateSpecial(spIdx, "condition_value", id);
      setNewLevelName("");
      toast.success("Nivel creado");
    } catch {
      toast.error("No se pudo crear el nivel");
    }
  };

  const conditionValueControl = (sp, spIdx) => {
    if (sp.condition_type === "client_tag") {
      return (
        <div className="flex gap-2">
          <select
            value={clientTags.includes(sp.condition_value) ? sp.condition_value : (sp.condition_value === "__new_tag__" ? "__new_tag__" : "")}
            onChange={(e) => {
              if (e.target.value === "__new_tag__") {
                updateSpecial(spIdx, "condition_value", "__new_tag__");
              } else {
                updateSpecial(spIdx, "condition_value", e.target.value);
                setNewTagName("");
              }
            }}
            className="min-w-0 flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm"
          >
            <option value="">Selecciona etiqueta</option>
            {clientTags.map((tag) => <option key={tag} value={tag}>{tag}</option>)}
            <option value="__new_tag__">+ Nueva etiqueta…</option>
          </select>
          {sp.condition_value === "__new_tag__" && (
            <>
              <Input value={newTagName || sp.condition_value || ""} onChange={(e) => setNewTagName(e.target.value)} placeholder="Nueva etiqueta" />
              <Button type="button" variant="outline" onClick={() => createClientTag(spIdx)} className="text-xs font-black">Crear</Button>
            </>
          )}
        </div>
      );
    }

    if (sp.condition_type === "client_level") {
      const levelIds = (loyaltyLevels || []).map((l) => l.id);
      const isCreating = sp.condition_value === "__new_level__";
      return (
        <div className="flex gap-2">
          <select
            value={levelIds.includes(sp.condition_value) ? sp.condition_value : (isCreating ? "__new_level__" : "")}
            onChange={(e) => {
              if (e.target.value === "__new_level__") {
                updateSpecial(spIdx, "condition_value", "__new_level__");
              } else {
                updateSpecial(spIdx, "condition_value", e.target.value);
                setNewLevelName("");
              }
            }}
            className="min-w-0 flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm"
          >
            <option value="">Selecciona nivel</option>
            {(loyaltyLevels || []).map((level) => (
              <option key={level.id} value={level.id}>{level.icon || "⭐"} {level.label}</option>
            ))}
            <option value="__new_level__">+ Nuevo nivel…</option>
          </select>
          {isCreating && (
            <>
              <Input value={newLevelName} onChange={(e) => setNewLevelName(e.target.value)} placeholder="Nuevo nivel" />
              <Button type="button" variant="outline" onClick={() => createLoyaltyLevel(spIdx)} className="text-xs font-black">Crear</Button>
            </>
          )}
        </div>
      );
    }

    if (sp.condition_type === "client_id") {
      const selectedClient = (clients || []).find((c) => c.id === sp.condition_value);
      const q = clientSearch.trim().toLowerCase();

      const matches = (clients || [])
        .filter((c) => {
          if (!q) return false;
          return [
            c.name,
            c.email,
            c.phone,
            c.client_name,
            c.client_email,
            c.client_phone,
          ].some((v) => String(v || "").toLowerCase().includes(q));
        })
        .slice(0, 8);

      return (
        <div className="space-y-2">
          {selectedClient ? (
            <div className="flex items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
              <div className="min-w-0">
                <div className="font-black text-emerald-900">{selectedClient.name || selectedClient.email || "Cliente"}</div>
                <div className="truncate text-xs font-bold text-emerald-700">
                  {selectedClient.phone || "Sin teléfono"} · {selectedClient.email || "Sin email"}
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  updateSpecial(spIdx, "condition_value", "");
                  setClientSearch("");
                }}
                className="text-xs font-black"
              >
                Cambiar
              </Button>
            </div>
          ) : (
            <>
              <Input
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
                placeholder="Buscar cliente por nombre, teléfono o email…"
              />
              {q && (
                <div className="max-h-52 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
                  {matches.length > 0 ? matches.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        updateSpecial(spIdx, "condition_value", c.id);
                        setClientSearch("");
                      }}
                      className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-lime-50"
                    >
                      <div className="font-black text-slate-900">{c.name || c.email || "Cliente"}</div>
                      <div className="text-xs font-bold text-slate-500">
                        {c.phone || "Sin teléfono"} · {c.email || "Sin email"}
                      </div>
                    </button>
                  )) : (
                    <div className="px-3 py-2 text-sm font-bold text-slate-500">
                      Sin resultados para “{clientSearch}”
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      );
    }

    return (
      <Input value={sp.condition_value || ""} onChange={(e) => updateSpecial(spIdx, "condition_value", e.target.value)} placeholder="Valor condición" />
    );
  };

  const filtered = items.filter((item) => {
    const q = search.trim().toLowerCase();
    if (categoryFilter !== "all" && (item.category || "general") !== categoryFilter) return false;
    if (!q) return true;
    return [item.name, item.slug, item.category, item.status, item.pricing_type]
      .some((v) => String(v || "").toLowerCase().includes(q));
  });

  const byCategory = filtered.reduce((acc, item) => {
    const key = item.category || "general";
    acc[key] = acc[key] || [];
    acc[key].push(item);
    return acc;
  }, {});

  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center rounded-full bg-lime-100 px-3 py-1 text-xs font-black uppercase tracking-widest text-lime-800">
            <Package className="mr-1 h-4 w-4" /> Catálogo Comercial
          </div>
          <h1 className="mt-3 text-3xl font-black text-slate-900">Servicios, productos y tarifas</h1>
          <p className="mt-1 max-w-3xl text-sm font-semibold text-slate-500">
            Base configurable de KAPEI. Los campos de tiempo y puntos son opcionales y quedan preparados para futuras estadísticas.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className="mr-2 h-4 w-4" /> Recargar
          </Button>
          <Button type="button" onClick={seed} disabled={seeding} className="bg-[#4d7c0f] font-black text-white hover:bg-[#3f6a0c]">
            <Sparkles className="mr-2 h-4 w-4" /> {seeding ? "Creando…" : "Crear ejemplos"}
          </Button>
          <Button type="button" variant="outline" disabled title="Próximamente">
            <Plus className="mr-2 h-4 w-4" /> Nuevo servicio
          </Button>
        </div>
      </div>

      <div className="mb-5 grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[1fr_260px]">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar…" className="pl-9" />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="rounded-md border border-slate-200 px-3 py-2 text-sm font-bold"
        >
          <option value="all">Todas las categorías</option>
          {catalogCategories.map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center font-bold text-slate-500">Cargando catálogo…</div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl border-2 border-dashed border-lime-300 bg-lime-50 p-10 text-center">
          <div className="text-5xl">📦</div>
          <h2 className="mt-3 text-2xl font-black text-slate-900">Catálogo vacío</h2>
          <Button type="button" onClick={seed} disabled={seeding} className="mt-5 bg-[#4d7c0f] font-black text-white hover:bg-[#3f6a0c]">
            <Sparkles className="mr-2 h-4 w-4" /> {seeding ? "Creando…" : "Crear catálogo de ejemplo"}
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(byCategory)
            .sort(([a], [b]) => (categoryOrder[a] || 1000) - (categoryOrder[b] || 1000) || a.localeCompare(b))
            .map(([category, categoryItems]) => (
            <section key={category} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between gap-2 border-b border-slate-100 pb-3">
                <h2 className="text-lg font-black uppercase tracking-widest text-slate-800">{category}</h2>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-500">{categoryItems.length} item{categoryItems.length === 1 ? "" : "s"}</span>
              </div>

              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {categoryItems.map((item) => (
                  <button key={item.id} type="button" onClick={() => openEdit(item)} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left hover:border-lime-300 hover:bg-lime-50/40">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{item.icon || "📦"}</span>
                          <div className="truncate text-sm font-black text-slate-900">{item.name}</div>
                        </div>
                        <div className="mt-1 truncate font-mono text-[10px] font-bold text-slate-400">{item.slug}</div>
                      </div>
                      <span className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-black uppercase ${statusClass[item.status] || "bg-slate-100 text-slate-600"}`}>
                        {statusLabel[item.status] || item.status || "—"}
                      </span>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      <div className="rounded-xl bg-white p-2"><div className="font-black uppercase text-slate-400">Precio</div><div className="font-black text-slate-900">{money(item.price_eur)}</div></div>
                      <div className="rounded-xl bg-white p-2"><div className="font-black uppercase text-slate-400">Tipo</div><div className="font-black text-slate-900">{item.pricing_type || "—"}</div></div>
                      <div className="rounded-xl bg-white p-2"><div className="font-black uppercase text-slate-400">Tiempo</div><div className="font-black text-slate-900">{minutes(item.estimated_time_seconds)}</div></div>
                      <div className="rounded-xl bg-white p-2"><div className="font-black uppercase text-slate-400">Puntos</div><div className="font-black text-slate-900">{item.production_points || "—"}</div></div>
                    </div>

                    {item.price_rules?.length > 0 && (
                      <div className="mt-3 rounded-xl bg-white p-2 text-xs font-bold text-slate-600">
                        {item.price_rules.length} regla{item.price_rules.length === 1 ? "" : "s"} de precio
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
          <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="mb-5 flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-black uppercase tracking-widest text-lime-700">Editar servicio</div>
                <h2 className="text-2xl font-black text-slate-900">{editing.name}</h2>
              </div>
              <button type="button" onClick={() => setEditing(null)} className="rounded-full bg-slate-100 p-2">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-5 flex flex-wrap gap-2 rounded-2xl bg-slate-100 p-1 text-xs font-black uppercase">
              {[
                ["general", "General"],
                ["precio", "Precio"],
                ["reglas", "Reglas"],
                ["compatibilidad", "Compatibilidad"],
                ["configurador", "Configurador"],
                ["defaults", "Por defecto"],
                ["ia", "IA"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setCatalogTab(key)}
                  className={`rounded-xl px-3 py-2 ${
                    catalogTab === key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:bg-white/60"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {catalogTab === "general" && (
              <div className="space-y-4">
                <div className="grid gap-3 md:grid-cols-2">
                  <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Nombre" />
                  <Input value={form.icon} onChange={(e) => setForm({...form, icon: e.target.value})} placeholder="Icono emoji" />
                  <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="rounded-md border border-slate-200 px-3 py-2 text-sm">
                    {catalogCategories.map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                  <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} className="rounded-md border border-slate-200 px-3 py-2 text-sm">
                    <option value="active">Activo</option>
                    <option value="staff_only">Solo Staff</option>
                    <option value="hidden">Oculto</option>
                    <option value="coming_soon">Próximamente</option>
                  </select>
                </div>

                <div className="grid gap-2 text-sm font-bold text-slate-700 md:grid-cols-2">
                  <label className="flex items-center gap-2 rounded-xl bg-slate-50 p-3">
                    <input type="checkbox" checked={form.visible_staff} onChange={(e) => setForm({...form, visible_staff: e.target.checked})} />
                    Visible para Staff
                  </label>
                  <label className="flex items-center gap-2 rounded-xl bg-slate-50 p-3">
                    <input type="checkbox" checked={form.visible_client} onChange={(e) => setForm({...form, visible_client: e.target.checked})} />
                    Visible para cliente
                  </label>
                </div>

                <textarea
                  value={form.description}
                  onChange={(e) => setForm({...form, description: e.target.value})}
                  placeholder="Descripción / notas internas"
                  className="min-h-[90px] w-full rounded-xl border border-slate-200 p-3 text-sm outline-none focus:ring-2 focus:ring-lime-300"
                />
              </div>
            )}

            {catalogTab === "precio" && (
              <div className="space-y-5">
                <div className="grid gap-3 md:grid-cols-2">
                  <select value={form.pricing_type} onChange={(e) => setForm({...form, pricing_type: e.target.value})} className="rounded-md border border-slate-200 px-3 py-2 text-sm">
                    <option value="fixed">Fijo</option>
                    <option value="range">Por rangos</option>
                    <option value="unit">Por unidad</option>
                    <option value="area">Por m²</option>
                    <option value="manual">Manual</option>
                    <option value="percent">Porcentaje</option>
                    <option value="multiplier">Multiplicador</option>
                  </select>
                  <Input value={form.unit} onChange={(e) => setForm({...form, unit: e.target.value})} placeholder="Unidad" />
                  {form.pricing_type === "range" ? (
                    <div className="rounded-md border border-lime-200 bg-lime-50 px-3 py-2 text-sm font-bold text-lime-800">
                      Precio por rangos: usa la tabla inferior.
                    </div>
                  ) : (
                    <Input value={form.price_eur} onChange={(e) => setForm({...form, price_eur: e.target.value})} placeholder="Precio cliente €" />
                  )}
                  <Input value={form.cost_eur} onChange={(e) => setForm({...form, cost_eur: e.target.value})} placeholder="Coste interno €" />
                  <Input value={form.vat_percent} onChange={(e) => setForm({...form, vat_percent: e.target.value})} placeholder="IVA %" />
                </div>

                {(form.pricing_type === "range" || (form.price_rules || []).length > 0) && (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <div>
                        <div className="text-xs font-black uppercase tracking-widest text-slate-500">Rangos de precio</div>
                        <div className="text-xs font-semibold text-slate-500">Deja “Hasta” vacío para infinito.</div>
                      </div>
                      <Button type="button" variant="outline" onClick={addRule} className="text-xs font-black">
                        + Añadir rango
                      </Button>
                    </div>

                    <div className="space-y-2">
                      {(form.price_rules || []).map((rule, idx) => (
                        <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2">
                          <Input value={rule.from ?? ""} onChange={(e) => updateRule(idx, "from", e.target.value)} placeholder="Desde" />
                          <Input value={rule.to ?? ""} onChange={(e) => updateRule(idx, "to", e.target.value)} placeholder="Hasta" />
                          <Input value={rule.unit_price_eur ?? ""} onChange={(e) => updateRule(idx, "unit_price_eur", e.target.value)} placeholder="Precio unidad €" />
                          <Button type="button" variant="outline" onClick={() => removeRule(idx)} className="px-3 text-xs font-black text-red-700">×</Button>
                        </div>
                      ))}
                      {(form.price_rules || []).length === 0 && (
                        <div className="rounded-xl bg-white p-3 text-sm font-bold text-slate-500">
                          Sin rangos todavía. Añade el primer tramo.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div>
                      <div className="text-xs font-black uppercase tracking-widest text-blue-700">Tarifas especiales</div>
                      <div className="text-xs font-semibold text-blue-700">Para estudiantes, empresas, niveles del Club o clientes concretos.</div>
                    </div>
                    <Button type="button" variant="outline" onClick={addSpecialRate} className="text-xs font-black">
                      + Tarifa especial
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {(form.special_price_rules || []).map((sp, spIdx) => (
                      <div key={spIdx} className="rounded-xl bg-white p-3">
                        <div className="mb-3 grid gap-2 md:grid-cols-[1fr_1fr_1fr_auto]">
                          <Input value={sp.name || ""} onChange={(e) => updateSpecial(spIdx, "name", e.target.value)} placeholder="Nombre tarifa" />
                          <select value={sp.condition_type || "client_tag"} onChange={(e) => {
                            updateSpecial(spIdx, "condition_type", e.target.value);
                            updateSpecial(spIdx, "condition_value", "");
                          }} className="rounded-md border border-slate-200 px-3 py-2 text-sm">
                            <option value="client_tag">Etiqueta cliente</option>
                            <option value="client_level">Nivel Club</option>
                            <option value="client_id">Cliente concreto</option>
                          </select>
                          {conditionValueControl(sp, spIdx)}
                          <div className="flex gap-2">
                            <Button type="button" variant="outline" onClick={() => duplicateSpecialRate(spIdx)} className="text-xs font-black">
                              Duplicar
                            </Button>
                            <Button type="button" variant="outline" onClick={() => removeSpecialRate(spIdx)} className="text-xs font-black text-red-700">
                              Eliminar
                            </Button>
                          </div>
                        </div>

                        <div className="space-y-2">
                          {(sp.price_rules || []).map((rule, ruleIdx) => (
                            <div key={ruleIdx} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2">
                              <Input value={rule.from ?? ""} onChange={(e) => updateSpecialRule(spIdx, ruleIdx, "from", e.target.value)} placeholder="Desde" />
                              <Input value={rule.to ?? ""} onChange={(e) => updateSpecialRule(spIdx, ruleIdx, "to", e.target.value)} placeholder="Hasta" />
                              <Input value={rule.unit_price_eur ?? ""} onChange={(e) => updateSpecialRule(spIdx, ruleIdx, "unit_price_eur", e.target.value)} placeholder="Precio unidad €" />
                              <Button type="button" variant="outline" onClick={() => removeSpecialRule(spIdx, ruleIdx)} className="px-3 text-xs font-black text-red-700">×</Button>
                            </div>
                          ))}
                        </div>

                        <Button type="button" variant="outline" onClick={() => addSpecialRule(spIdx)} className="mt-2 text-xs font-black">
                          + Añadir rango especial
                        </Button>
                      </div>
                    ))}

                    {(form.special_price_rules || []).length === 0 && (
                      <div className="rounded-xl bg-white p-3 text-sm font-bold text-blue-700">
                        Sin tarifas especiales. Puedes añadir una para estudiantes con etiqueta “estudiante”.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {catalogTab === "reglas" && (
              <div className="space-y-4">
                <div className="grid gap-3 md:grid-cols-2">
                  <select value={form.service_type || "service"} onChange={(e) => setForm({...form, service_type: e.target.value})} className="rounded-md border border-slate-200 px-3 py-2 text-sm">
                    <option value="service">Servicio principal</option>
                    <option value="material">Material</option>
                    <option value="finish">Acabado / servicio asociado</option>
                    <option value="extra">Extra</option>
                    <option value="labor">Mano de obra</option>
                  </select>
                  <select value={form.calculation_basis || "unit"} onChange={(e) => setForm({...form, calculation_basis: e.target.value})} className="rounded-md border border-slate-200 px-3 py-2 text-sm">
                    <option value="unit">Unidad</option>
                    <option value="page">Página</option>
                    <option value="sheet">Hoja</option>
                    <option value="document">Documento</option>
                    <option value="sheets_per_document">Hojas por documento</option>
                    <option value="area">m²</option>
                    <option value="linear_meter">Metro lineal</option>
                  </select>
                  <Input value={form.min_sheets} onChange={(e) => setForm({...form, min_sheets: e.target.value})} placeholder="Mínimo hojas" />
                  <Input value={form.max_sheets} onChange={(e) => setForm({...form, max_sheets: e.target.value})} placeholder="Máximo hojas" />
                  <Input value={form.estimated_time_seconds} onChange={(e) => setForm({...form, estimated_time_seconds: e.target.value})} placeholder="Tiempo estimado segundos" />
                  <Input value={form.production_points} onChange={(e) => setForm({...form, production_points: e.target.value})} placeholder="Puntos producción" />
                </div>

                <div className="grid gap-2 text-sm font-bold text-slate-700 md:grid-cols-2">
                  <label className="flex items-center gap-2 rounded-xl bg-slate-50 p-3">
                    <input type="checkbox" checked={!!form.independent_service} onChange={(e) => setForm({...form, independent_service: e.target.checked})} />
                    Servicio independiente
                  </label>
                  <label className="flex items-center gap-2 rounded-xl bg-slate-50 p-3">
                    <input type="checkbox" checked={!!form.attachable_service} onChange={(e) => setForm({...form, attachable_service: e.target.checked})} />
                    Asociable a otros servicios
                  </label>
                </div>

                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm font-bold text-amber-900">
                  Estas reglas preparan el motor de presupuestos. No afectan todavía a la app cliente.
                </div>
              </div>
            )}

            {catalogTab === "compatibilidad" && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm font-bold text-slate-500">
                Próximamente: compatibilidad con materiales, acabados, servicios sugeridos y árbol del asistente.
              </div>
            )}

            {catalogTab === "configurador" && (
              <div className="space-y-4">
                <div className="rounded-2xl border border-lime-200 bg-lime-50 p-4">
                  <div className="text-xs font-black uppercase tracking-widest text-lime-700">Opciones del configurador cliente</div>
                  <div className="mt-1 text-sm font-bold text-lime-900">
                    Marca qué botones/opciones debe mostrar la app cliente para este servicio.
                  </div>
                </div>

                <div className="grid gap-2 md:grid-cols-2">
                  {configuratorOptions.map(([key, label]) => {
                    const option = (form.options || []).find((o) => o.key === key);
                    const checked = !!option;
                    return (
                      <div key={key} className={`rounded-xl border p-3 ${
                        checked ? "border-lime-300 bg-lime-50 text-lime-900" : "border-slate-200 bg-slate-50 text-slate-600"
                      }`}>
                        <label className="flex items-center gap-3 text-sm font-black">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleConfiguratorOption(key, label)}
                          />
                          {label}
                        </label>

                        {checked && (
                          <div className="mt-3 grid gap-2 text-xs md:grid-cols-4">
                            <label className="flex items-center gap-2 rounded-lg bg-white/80 px-2 py-2 font-bold">
                              <input
                                type="checkbox"
                                checked={!!option.required}
                                onChange={(e) => {
                                  setForm((prev) => ({
                                    ...prev,
                                    options: (prev.options || []).map((o) =>
                                      o.key === key ? { ...o, required: e.target.checked } : o
                                    ),
                                  }));
                                }}
                              />
                              Obligatorio
                            </label>

                            <Input
                              value={option.order ?? ""}
                              onChange={(e) => {
                                setForm((prev) => ({
                                  ...prev,
                                  options: (prev.options || []).map((o) =>
                                    o.key === key ? { ...o, order: e.target.value } : o
                                  ),
                                }));
                              }}
                              placeholder="Orden"
                            />

                            <Input
                              value={option.default_value ?? ""}
                              onChange={(e) => {
                                setForm((prev) => ({
                                  ...prev,
                                  options: (prev.options || []).map((o) =>
                                    o.key === key ? { ...o, default_value: e.target.value } : o
                                  ),
                                }));
                              }}
                              placeholder="Valor por defecto"
                            />

                            <select
                              value={option.source || ""}
                              onChange={(e) => {
                                setForm((prev) => ({
                                  ...prev,
                                  options: (prev.options || []).map((o) =>
                                    o.key === key ? { ...o, source: e.target.value } : o
                                  ),
                                }));
                              }}
                              className="rounded-md border border-slate-200 px-2 py-2 text-xs font-bold"
                            >
                              <option value="">Origen manual</option>
                              <option value="materials">Materiales</option>
                              <option value="finishes">Acabados</option>
                              <option value="bindings">Encuadernaciones</option>
                              <option value="sizes">Tamaños</option>
                              <option value="colors">B/N y color</option>
                              <option value="sides">Caras</option>
                              <option value="custom">Personalizado</option>
                            </select>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm font-bold text-amber-900">
                  Esto prepara el futuro configurador del cliente. De momento no cambia el envío actual.
                </div>
              </div>
            )}

            {catalogTab === "defaults" && (
              <div className="space-y-4">
                <div className="rounded-2xl border border-lime-200 bg-lime-50 p-4">
                  <div className="text-xs font-black uppercase tracking-widest text-lime-700">
                    Configuración por defecto
                  </div>
                  <div className="mt-1 text-sm font-bold text-lime-900">
                    Esto se marcará automáticamente cuando el cliente elija este servicio. No se mostrará como recomendación: simplemente aparecerá ya seleccionado.
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-black uppercase tracking-widest text-slate-500">Copias</label>
                    <Input
                      type="number"
                      min="1"
                      value={form.default_configuration?.copies ?? 1}
                      onChange={(e) => updateDefaultConfig("copies", Number(e.target.value || 1))}
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-black uppercase tracking-widest text-slate-500">Tamaño</label>
                    <select
                      value={form.default_configuration?.size || "A4"}
                      onChange={(e) => updateDefaultConfig("size", e.target.value)}
                      className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-bold"
                    >
                      <option value="A4">A4</option>
                      <option value="A3">A3</option>
                      <option value="A6">A6</option>
                      <option value="10x15">Foto 10x15</option>
                      <option value="13x18">Foto 13x18</option>
                      <option value="15x20">Foto 15x20</option>
                      <option value="10x7">Foto 10x7</option>
                      <option value="10x10">Foto 10x10</option>
                      <option value="polaroid">Polaroid</option>
                      <option value="tira-15x5">Tira 15x5</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-black uppercase tracking-widest text-slate-500">Color</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        ["bw", "B/N"],
                        ["color", "Color"],
                      ].map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => updateDefaultConfig("color", value)}
                          className={`rounded-xl border-2 p-3 text-sm font-black ${
                            form.default_configuration?.color === value
                              ? "border-lime-500 bg-lime-50 text-lime-800"
                              : "border-slate-200 bg-white text-slate-600"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-black uppercase tracking-widest text-slate-500">Caras</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        ["single", "Una cara"],
                        ["double", "Doble cara"],
                      ].map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => updateDefaultConfig("sides", value)}
                          className={`rounded-xl border-2 p-3 text-sm font-black ${
                            form.default_configuration?.sides === value
                              ? "border-lime-500 bg-lime-50 text-lime-800"
                              : "border-slate-200 bg-white text-slate-600"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs font-black uppercase tracking-widest text-slate-500">Papel / material</label>
                    <Input
                      value={form.default_configuration?.paper || ""}
                      onChange={(e) => updateDefaultConfig("paper", e.target.value)}
                      placeholder="papel-80g, papel-brillo-150g, cartulina-brillo-350g..."
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs font-black uppercase tracking-widest text-slate-500">Acabados iniciales</label>
                    <div className="grid gap-2 md:grid-cols-2">
                      {items
                        .filter((x) => x.service_type === "finish" || ["acabados", "plastificado", "encuadernacion", "soportes"].includes(x.category))
                        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                        .slice(0, 60)
                        .map((x) => {
                          const slug = x.slug || x.id;
                          const checked = (form.default_configuration?.finishes || []).includes(slug);
                          return (
                            <button
                              key={slug}
                              type="button"
                              onClick={() => toggleDefaultFinish(slug)}
                              className={`rounded-xl border-2 p-3 text-left text-xs font-black ${
                                checked
                                  ? "border-lime-500 bg-lime-50 text-lime-800"
                                  : "border-slate-200 bg-white text-slate-600"
                              }`}
                            >
                              {x.icon || ""} {x.name}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {catalogTab === "ia" && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm font-bold text-slate-500">
                Próximamente: instrucciones IA, preguntas sugeridas y textos de ayuda.
              </div>
            )}

            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <Button type="button" variant="outline" onClick={duplicateService} disabled={saving} className="font-black">
                Duplicar servicio
              </Button>
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button type="button" onClick={saveEdit} disabled={saving} className="bg-[#4d7c0f] font-black text-white hover:bg-[#3f6a0c]">
                {saving ? "Guardando…" : "Guardar cambios"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
